import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Alert, View, Pressable, ActivityIndicator, Modal, Platform } from 'react-native';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme, useThemeSetting } from '@/theme';
import { auth } from '@/lib/firebase';
import { signOut } from 'firebase/auth';
import {
  useUserStore,
  type NotificationCategory,
  type NotificationPreference,
  type TravelType,
} from '@/state/userStore';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { usePremiumUpsellStore } from '@/state/premiumUpsellStore';
import { db } from '@/lib/firebase';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { BuildUpdateDiagnosticsCard } from '@/components/settings/BuildUpdateDiagnosticsCard';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import { OfflineSettingsScreen } from '@/components/offline/OfflineSettingsScreen';
import { getAvatarImage } from '@/constants/avatars';
import { ConnectedServicesModal } from '@/components/settings/ConnectedServicesModal';
import { checkInboxStatus, type InboxProviderId } from '@/services/inboxSync';
import { useInboxConnectionsStore } from '@/state/inboxConnectionsStore';
import { NotificationSettingsModal } from '@/components/settings/NotificationSettingsModal';
import { deleteAccount } from '@/services/accountDeletion';
import { exportAndDownloadUserData } from '@/services/dataExport';
import {
  createStripeCheckoutSession,
  isExternalCheckoutAllowed,
  refreshEntitlementState,
} from '@/services/billing';
import {
  getRevenueCatPremiumSnapshot,
  getRevenueCatInitStatus,
  logoutRevenueCatUser,
  presentRevenueCatPaywall,
  restoreRevenueCatPurchases,
  type PresentRevenueCatPaywallResult,
} from '@/services/revenuecat';
import { FREE_PLAN_LIMITS } from '@/types/entitlements';
import type { PlanEntitlement, EntitlementStatus, EntitlementProvider, PlanTier } from '@/types/entitlements';
import { saveLastKnownEntitlementUi } from '@/services/entitlementUiCache';
import { useOfflinePlanTierDisplay } from '@/hooks/useOfflinePlanTierDisplay';
import { removeFCMToken } from '@/services/fcmTokens';
import { cancelAllNotifications } from '@/services/notifications';
import { captureException, clearUserContext } from '@/services/errorTracking';
import { logger } from '@/services/logger';
import { resetNotificationsState } from '@/notifications/service';
import { clearStoredSmartCues } from '@/hooks/useSmartCueNotifications';
import { clearAuthBootstrapSnapshot } from '@/services/authBootstrapCache';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { networkMonitor } from '@/services/networkMonitor';
import { AppImage } from '@/components/ui/AppImage';
import { HelpAndFeedbackCard } from '@/components/settings/HelpAndFeedbackCard';
import { ApplePhase5DebugCard } from '@/components/settings/ApplePhase5DebugCard';
import { WeatherLocationDebugCard } from '@/components/settings/WeatherLocationDebugCard';

function paywallFailureAlert(result: PresentRevenueCatPaywallResult & { ok: false }): {
  title: string;
  message: string;
} {
  const storeName = Platform.OS === 'ios' ? 'App Store' : 'store';
  const testBuildName = Platform.OS === 'ios' ? 'TestFlight' : 'test build';
  switch (result.reason) {
    case 'web':
      return {
        title: 'Subscriptions',
        message: 'In-app subscriptions are only available in the installed mobile app.',
      };
    case 'disabled':
      return {
        title: 'Subscription unavailable',
        message: `Subscriptions are not enabled in this build. Install a production build from the ${storeName} or ${testBuildName}.`,
      };
    case 'missing_key':
      return {
        title: 'Subscription unavailable',
        message:
          `This install was built without ${storeName} billing keys, so subscriptions cannot load. Use a newer build from ${testBuildName} or ${storeName} once billing is configured.`,
      };
    case 'init_failed':
      return {
        title: 'In-app subscription',
        message:
          'Could not connect to subscription services. Check your connection and try again, or tap Restore purchases if you already subscribed.',
      };
    case 'no_offerings':
      return {
        title: 'Subscription unavailable',
        message:
          `We couldn’t load subscription plans. Confirm ${storeName} subscriptions are active and attached to a default Offering in RevenueCat (matching product IDs), then try again.`,
      };
    case 'present_failed':
      return {
        title: 'In-app subscription',
        message:
          'Could not open the subscription screen. Try again in a moment, or tap Restore purchases if you already subscribed.',
      };
    case 'init_pending':
    default:
      return {
        title: 'In-app subscription',
        message:
          'We could not open subscriptions on this device. Check your connection, try again in a moment, or tap Restore purchases if you already subscribed.',
      };
  }
}

traceModuleEvaluation('app/(tabs)/profile');

const StorageManagement = lazy(() =>
  import('@/components/settings/StorageManagement').then((module) => ({ default: module.StorageManagement }))
);

// Travel type labels mapping
const TRAVEL_TYPE_LABELS: Record<TravelType, string> = {
  solo: 'Solo travel',
  group: 'Group travel',
  business: 'Business travel',
  couple: 'Couple getaways',
  family: 'Family trips',
};

// Currency labels for display
const CURRENCY_LABELS: Record<string, string> = {
  USD: 'US Dollar ($)',
  EUR: 'Euro (€)',
  GBP: 'British Pound (£)',
  JPY: 'Japanese Yen (¥)',
  CAD: 'Canadian Dollar (C$)',
  AUD: 'Australian Dollar (A$)',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan (¥)',
  INR: 'Indian Rupee (₹)',
  BRL: 'Brazilian Real (R$)',
  MXN: 'Mexican Peso (MX$)',
  SGD: 'Singapore Dollar (S$)',
  HKD: 'Hong Kong Dollar (HK$)',
  NZD: 'New Zealand Dollar (NZ$)',
  ZAR: 'South African Rand',
  SEK: 'Swedish Krona',
  NOK: 'Norwegian Krone',
  DKK: 'Danish Krone',
  PLN: 'Polish Złoty',
  TRY: 'Turkish Lira',
};

const NOTIFICATION_LABELS: Record<NotificationCategory, string> = {
  itinerary: 'Itinerary',
  expenses: 'Expenses',
  safety: 'Safety',
  documents: 'Document nudges',
  collaboration: 'Collaborator updates & comments',
};

type PreferenceItem = {
  id: string;
  label: string;
  value: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress?: () => void;
  status?: 'connected' | 'disconnected' | 'loading';
};

type ProfileScreenProps = {
  hideHeader?: boolean;
};

function ProfileScreenInner({ hideHeader = false }: ProfileScreenProps = {}) {
  const theme = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams();
  const reset = useUserStore((state) => state.reset);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const profile = useUserStore((state) => state.profile);
  const setProfileDetails = useUserStore((state) => state.setProfileDetails);
  const offlinePlan = useOfflinePlanTierDisplay(firebaseUser?.uid, {
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });
  const premiumOnProfile = offlinePlan.effectivePremium;
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const closePremiumGate = usePremiumGateStore((s) => s.close);
  const openMarketingUpsell = usePremiumUpsellStore((s) => s.open);
  const closeMarketingUpsell = usePremiumUpsellStore((s) => s.close);
  const [loggingOut, setLoggingOut] = useState(false);
  const inboxConnections = useInboxConnectionsStore((s) => s.connections);
  const inboxConnectionsLoading = useInboxConnectionsStore((s) => s.loading);
  const inboxConnectionsFetchedAt = useInboxConnectionsStore((s) => s.lastFetchedAt);
  /** True until we've received at least one snapshot for this session (tabs layout orchestrates refresh). */
  const checkingProviders = Boolean(firebaseUser?.uid)
    ? inboxConnectionsLoading || inboxConnectionsFetchedAt === null
    : false;
  const [showConnectedServices, setShowConnectedServices] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showUpgradeFlowModal, setShowUpgradeFlowModal] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [refreshingEntitlement, setRefreshingEntitlement] = useState(false);
  const [startingCheckout, setStartingCheckout] = useState<'monthly' | 'annual' | null>(null);
  const [webCheckoutInterval, setWebCheckoutInterval] = useState<'monthly' | 'annual'>('annual');
  const [presentingPaywall, setPresentingPaywall] = useState(false);
  const [memberSinceYear, setMemberSinceYear] = useState<number | null>(null);
  const { preference: themePreference, setPreference: setThemePreference } = useThemeSetting();
  const { confirm: confirmAction, confirmAsync } = useConfirmDialog();
  const { isAdmin } = useIsAdmin();
  const encodedReturnToProfile = useMemo(() => encodeURIComponent('/(tabs)/profile'), []);
  // Prefer Tripvio storage keys; keep legacy TripFlow key as fallback for older installs.
  const OPEN_OFFLINE_SETTINGS_KEY = '@tripvio_open_offline_settings';
  const LEGACY_OPEN_OFFLINE_SETTINGS_KEY = '@tripflow_open_offline_settings';

  // Deep-link helper: open Offline Mode settings when requested via route param.
  useEffect(() => {
    const openOffline = params.openOffline === '1' || params.openOffline === 'true';
    if (!openOffline) return;
    if (!premiumOnProfile) {
      openPremiumGate({
        feature: 'offline_mode',
        title: 'Offline Mode',
        message: 'Offline Mode is a Premium feature. Upgrade to download maps, itineraries, and documents for offline use.',
        source: 'feature_gate',
      });
      try {
        router.setParams({ openOffline: undefined });
      } catch {
        // ignore
      }
      return;
    }

    setShowOfflineModal(true);
    try {
      router.setParams({ openOffline: undefined });
    } catch {
      // Router may not be ready; ignore.
    }
  }, [params.openOffline, router, premiumOnProfile, openPremiumGate]);

  // Deep-link: open upgrade/purchase flow when coming from Premium gate "Upgrade" button.
  useEffect(() => {
    const openUpgrade = params.openUpgrade === '1' || params.openUpgrade === 'true';
    if (!openUpgrade) return;
    setShowUpgradeFlowModal(true);
    try {
      router.setParams({ openUpgrade: undefined });
    } catch {
      // ignore
    }
  }, [params.openUpgrade, router]);

  // Also support a one-shot "open offline" flag (used by Plan banner action).
  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      AsyncStorage.getItem(OPEN_OFFLINE_SETTINGS_KEY)
        .then((value) => {
          if (cancelled) return;
          if (value === '1') {
            if (!premiumOnProfile) {
              openPremiumGate({
                feature: 'offline_mode',
                title: 'Offline Mode',
                message: 'Offline Mode is a Premium feature.',
                source: 'feature_gate',
              });
              AsyncStorage.removeItem(OPEN_OFFLINE_SETTINGS_KEY).catch(() => {});
              return;
            }

            setShowOfflineModal(true);
            AsyncStorage.removeItem(OPEN_OFFLINE_SETTINGS_KEY).catch(() => {});
          }
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }, [premiumOnProfile, openPremiumGate]),
  );

  // One-time migration: if the old key is set, move it to the new key.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const legacy = await AsyncStorage.getItem(LEGACY_OPEN_OFFLINE_SETTINGS_KEY);
        if (cancelled) return;
        if (legacy === '1') {
          await AsyncStorage.setItem(OPEN_OFFLINE_SETTINGS_KEY, '1');
          await AsyncStorage.removeItem(LEGACY_OPEN_OFFLINE_SETTINGS_KEY);
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Get member since year from Firebase user or Firestore
  useEffect(() => {
    const fetchMemberSince = async () => {
      if (!firebaseUser?.uid) return;

      try {
        // Try to get from Firestore user document first
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.createdAt) {
            const createdAt = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
            setMemberSinceYear(createdAt.getFullYear());
            return;
          }
        }

        // Fallback: try to get from Firebase Auth user metadata
        const currentUser = auth.currentUser;
        if (currentUser?.metadata?.creationTime) {
          const created = new Date(currentUser.metadata.creationTime);
          setMemberSinceYear(created.getFullYear());
        } else {
          // Final fallback: use current year
          setMemberSinceYear(new Date().getFullYear());
        }
      } catch (error) {
        console.error('Failed to fetch member since date:', error);
        setMemberSinceYear(new Date().getFullYear());
      }
    };

    void fetchMemberSince();
  }, [firebaseUser?.uid]);

  // Format user name and location
  const userDisplayName = useMemo(() => {
    if (profile.name) {
      return profile.name;
    }
    if (firebaseUser?.email) {
      return firebaseUser.email.split('@')[0];
    }
    return 'User';
  }, [profile.name, firebaseUser?.email]);

  const locationDisplay = useMemo(() => {
    if (profile.homeCountry) {
      return `Home base: ${profile.homeCountry}`;
    }
    return 'Home base not set';
  }, [profile.homeCountry]);

  // Format travel type
  const travelTypeDisplay = useMemo(() => {
    if (profile.travelType && TRAVEL_TYPE_LABELS[profile.travelType]) {
      return TRAVEL_TYPE_LABELS[profile.travelType];
    }
    return 'Not set';
  }, [profile.travelType]);

  // Format default currency
  const currencyDisplay = useMemo(() => {
    if (profile.defaultCurrency && CURRENCY_LABELS[profile.defaultCurrency]) {
      return `${profile.defaultCurrency} - ${CURRENCY_LABELS[profile.defaultCurrency]}`;
    }
    return 'USD - US Dollar ($)';
  }, [profile.defaultCurrency]);

  const notificationPreferenceDisplay = useMemo(() => {
    const prefs = profile.notificationPreference;
    if (prefs && typeof prefs === 'object') {
      const keys = Object.keys(NOTIFICATION_LABELS) as NotificationCategory[];
      const enabled = keys.filter((key) => prefs[key]);
      if (enabled.length === 0) return 'Select channels';
      if (enabled.length === keys.length) return 'All notifications';
      return enabled.map((key) => NOTIFICATION_LABELS[key]).join(', ');
    }
    return 'Select channels';
  }, [profile.notificationPreference]);

  // Format integrations display
  const integrationsDisplay = useMemo(() => {
    if (checkingProviders) {
      return 'Checking…';
    }
    const services: string[] = [];
    if (inboxConnections.gmail) services.push('Gmail');
    if (inboxConnections.outlook) services.push('Outlook');
    if (inboxConnections.icloud) services.push('iCloud');
    return services.length > 0 ? services.join(', ') : 'None connected';
  }, [
    checkingProviders,
    inboxConnections.gmail,
    inboxConnections.outlook,
    inboxConnections.icloud,
  ]);

  // Format appearance (theme) display
  const appearanceDisplay = useMemo(() => {
    if (themePreference === 'light') return 'Light';
    if (themePreference === 'dark') return 'Dark';
    return 'System (use device setting)';
  }, [themePreference]);

  // Handle edit profile
  const handleEditProfile = useCallback(() => {
    router.push(`/(onboarding)/profile-details?mode=edit&returnTo=${encodedReturnToProfile}`);
  }, [router, encodedReturnToProfile]);

  const handleEditAvatar = useCallback(() => {
    router.push(`/(onboarding)/avatar?mode=edit&returnTo=${encodedReturnToProfile}`);
  }, [router, encodedReturnToProfile]);

  // Handle edit travel type
  const handleEditTravelType = useCallback(() => {
    router.push(`/(onboarding)/travel-type?mode=edit&returnTo=${encodedReturnToProfile}`);
  }, [router, encodedReturnToProfile]);

  const handleConnectedServices = useCallback(() => {
    setShowConnectedServices(true);
  }, []);

  const handleNotificationSettings = useCallback(() => {
    setShowNotificationSettings(true);
  }, []);

  const handleAppearancePress = useCallback(() => {
    setShowThemeModal(true);
  }, []);

  // Build preferences array dynamically
  const preferences = useMemo<PreferenceItem[]>(() => {
    return [
      {
        id: 'appearance',
        label: 'Appearance',
        value: appearanceDisplay,
        icon: 'contrast-outline',
        onPress: handleAppearancePress,
      },
      {
        id: 'travel-type',
        label: 'Travel style',
        value: travelTypeDisplay,
        icon: 'airplane-outline',
        onPress: handleEditTravelType,
      },
      {
        id: 'currency',
        label: 'Default currency',
        value: currencyDisplay,
        icon: 'wallet-outline',
        onPress: handleEditProfile,
      },
      {
        id: 'notifications',
        label: 'Notifications',
        value: notificationPreferenceDisplay,
        icon: 'notifications-outline',
        onPress: handleNotificationSettings,
      },
      {
        id: 'integrations',
        label: 'Connected services',
        value: integrationsDisplay,
        icon: 'link-outline',
        onPress: handleConnectedServices,
        status: checkingProviders
          ? 'loading'
          : inboxConnections.gmail || inboxConnections.outlook || inboxConnections.icloud
            ? 'connected'
            : 'disconnected',
      },
    ];
  }, [
    appearanceDisplay,
    travelTypeDisplay,
    currencyDisplay,
    integrationsDisplay,
    notificationPreferenceDisplay,
    checkingProviders,
    inboxConnections.gmail,
    inboxConnections.outlook,
    inboxConnections.icloud,
    handleAppearancePress,
    handleEditTravelType,
    handleConnectedServices,
    handleEditProfile,
    handleNotificationSettings,
  ]);

  const handleLogout = async () => {
    console.log('[Profile] handleLogout called');
    
    if (loggingOut) {
      console.log('[Profile] Logout already in progress, ignoring');
      return; // Prevent multiple simultaneous logout attempts
    }

    console.log('[Profile] Starting logout process');
    setLoggingOut(true);

    try {
      const uid = firebaseUser?.uid || auth.currentUser?.uid;

      if (uid) {
        await removeFCMToken(uid).catch((error) => {
          const message = error instanceof Error ? error.message : String(error);
          console.warn('[Profile] Failed to remove FCM token during logout', { message });
        });
        await clearAuthBootstrapSnapshot(uid).catch((error) => {
          const message = error instanceof Error ? error.message : String(error);
          console.warn('[Profile] Failed to clear auth bootstrap snapshot during logout', { message });
        });
      }

      await Promise.all([
        cancelAllNotifications(),
        resetNotificationsState(),
        clearStoredSmartCues(),
      ]);

      closePremiumGate();
      closeMarketingUpsell();
      clearUserContext();
      await logoutRevenueCatUser().catch((error) => {
        const message = error instanceof Error ? error.message : String(error);
        console.warn('[Profile] Failed to log out RevenueCat during logout', { message });
      });
      
      // Sign out from Firebase
      console.log('[Profile] Signing out from Firebase');
      await signOut(auth);
      console.log('[Profile] Successfully signed out from Firebase');

      console.log('[Profile] Resetting store state');
      reset();
      
      // Small delay to ensure auth state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Navigate to root - index.tsx will handle redirect based on auth state
      console.log('[Profile] Navigating to root');
      router.replace('/');
      
      setLoggingOut(false);
      console.log('[Profile] Logout completed successfully');
    } catch (error: any) {
      console.error('[Profile] Failed to log out:', error);
      setLoggingOut(false);
      
      // Show error but still try to navigate
      Alert.alert(
        'Log out failed', 
        error?.message || 'Something went wrong while signing you out. You may need to restart the app.',
        [
          {
            text: 'OK',
            onPress: () => {
              // Try to navigate anyway
              try {
                console.log('[Profile] Attempting navigation after error');
                router.replace('/');
              } catch (navError) {
                console.error('[Profile] Navigation failed after logout error:', navError);
              }
            },
          },
        ]
      );
    }
  };

  const confirmLogout = () => {
    console.log('[Profile] confirmLogout called');
    
    if (loggingOut) {
      console.log('[Profile] Already logging out, ignoring');
      return;
    }

    console.log('[Profile] Showing logout confirmation dialog');
    confirmAction({
      title: 'Log out',
      message: 'Are you sure you want to sign out of Tripvio?',
      confirmText: 'Log out',
      cancelText: 'Cancel',
      onConfirm: handleLogout,
      onCancel: () => console.log('[Profile] Logout cancelled'),
    });
  };

  const handleDeleteAccount = async () => {
    const confirmed = await confirmAsync({
      title: 'Delete Account',
      message:
        'Are you absolutely sure you want to permanently delete your account? This will:\n\n• Delete all your trips and itineraries\n• Delete all bookings and expenses\n• Delete all documents and photos\n• Remove you from collaborative trips\n• Permanently delete your profile\n\nThis action cannot be undone.',
      confirmText: 'Delete Forever',
      cancelText: 'Cancel',
      destructive: true,
    });

    if (!confirmed) return;

    const finalConfirmed = await confirmAsync({
      title: 'Final Confirmation',
      message:
        'This is your last chance to cancel. Your account and all data will be permanently deleted and cannot be recovered. Are you absolutely certain?',
      confirmText: 'Yes, Delete Everything',
      cancelText: 'Cancel',
      destructive: true,
    });

    if (!finalConfirmed) return;

    try {
      setLoggingOut(true);
      await deleteAccount();

      Alert.alert(
        'Account Deleted',
        'Your account and all associated data have been permanently deleted.',
        [
          {
            text: 'OK',
            onPress: () => {
              reset();
              router.replace('/(onboarding)/value-proposition');
            },
          },
        ],
      );
    } catch (error: any) {
      setLoggingOut(false);
      const errorMessage = error?.message || 'Failed to delete account. Please try again or contact support.';
      Alert.alert('Deletion Failed', errorMessage);
    }
  };

  const handleRefreshEntitlement = async () => {
    try {
      setRefreshingEntitlement(true);
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
      const isSnapshotPremium = (snapshot: Awaited<ReturnType<typeof refreshEntitlementState>>) => {
        const status = snapshot.subscriptionStatus;
        return (
          snapshot.isPremium ||
          snapshot.planTier === 'premium' ||
          status === 'active' ||
          status === 'grace_period'
        );
      };

      const uid = firebaseUser?.uid || auth.currentUser?.uid;
      // First try a native store restore/sync so recent purchases are pulled into RevenueCat.
      const restoreAttempted = await restoreRevenueCatPurchases(uid);
      let snapshot = await refreshEntitlementState();
      let revenueCatSnapshot = await getRevenueCatPremiumSnapshot(uid);
      // Webhook propagation can lag right after restore/sync; retry a couple times before final UI message.
      if (restoreAttempted && !isSnapshotPremium(snapshot) && !revenueCatSnapshot.isPremium) {
        const retryDelaysMs = [1200, 2500];
        for (const delayMs of retryDelaysMs) {
          await sleep(delayMs);
          snapshot = await refreshEntitlementState();
          revenueCatSnapshot = await getRevenueCatPremiumSnapshot(uid);
          if (isSnapshotPremium(snapshot) || revenueCatSnapshot.isPremium) break;
        }
      }

      const normalizedPremium = isSnapshotPremium(snapshot) || revenueCatSnapshot.isPremium;
      const sub = snapshot.subscriptionStatus;
      const status: EntitlementStatus =
        revenueCatSnapshot.isPremium
          ? 'active'
          : sub === 'active' ||
              sub === 'grace_period' ||
              sub === 'cancelled' ||
              sub === 'expired'
          ? sub
          : 'inactive';
      const providerRaw = snapshot.entitlementProvider;
      const provider: EntitlementProvider =
        revenueCatSnapshot.isPremium
          ? 'revenuecat'
          : providerRaw === 'stripe' || providerRaw === 'revenuecat' || providerRaw === 'unknown'
          ? providerRaw
          : 'manual';
      const entitlement: PlanEntitlement = {
        tier: normalizedPremium ? 'premium' : 'free',
        status,
        provider,
        expiresAt: revenueCatSnapshot.expiresAt ?? snapshot.entitlementExpiresAt ?? undefined,
      };
      const planTier = (snapshot.planTier === 'premium' || snapshot.planTier === 'free'
        ? snapshot.planTier
        : undefined) as PlanTier | undefined;
      const nextPlanTier = normalizedPremium ? 'premium' : planTier ?? entitlement.tier;
      setProfileDetails({
        isPremium: normalizedPremium,
        planTier: nextPlanTier,
        entitlement,
      });
      void saveLastKnownEntitlementUi(uid, {
        isPremium: normalizedPremium,
        planTier: nextPlanTier,
        entitlement,
      });
      Alert.alert(
        normalizedPremium ? 'Premium active' : 'Account updated',
        normalizedPremium
          ? 'Your Premium subscription is active on this device. It may take a moment to sync across services.'
          : restoreAttempted
            ? 'Restore completed, but no active Premium subscription was found for this account.'
            : 'Your subscription status has been refreshed.'
      );
    } catch (error) {
      console.error('[Profile] Failed to refresh entitlement', error);
      Alert.alert('Unable to refresh', 'Could not refresh subscription status right now. Please try again.');
    } finally {
      setRefreshingEntitlement(false);
    }
  };

  // Render avatar
  const renderAvatar = () => {
    if (profile.avatar?.type === 'preset') {
      const avatarImage = getAvatarImage(profile.avatar.id);
      if (avatarImage) {
        return (
          <AppImage
            source={avatarImage}
            style={{
              width: 64,
              height: 64,
              borderRadius: 32,
              backgroundColor: theme.colors.borderMuted,
            }}
            resizeMode="cover"
          />
        );
      }
      // Fallback if avatar image not found
      return (
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            backgroundColor: theme.colors.primary,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ThemedText variant="hero" style={{ fontSize: 32 }}>
            👤
          </ThemedText>
        </View>
      );
    } else if (profile.avatar?.type === 'upload' && profile.avatar.uri) {
      return (
        <AppImage
          source={{ uri: profile.avatar.uri }}
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            backgroundColor: theme.colors.borderMuted,
          }}
        />
      );
    } else {
      // Default avatar
      return (
        <View
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            backgroundColor: theme.colors.primary,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Ionicons name="person" size={32} color={theme.colors.surface} />
        </View>
      );
    }
  };

  return (
    <Screen title={hideHeader ? undefined : "Profile & privacy"} subtitle={hideHeader ? undefined : "Control your data, preferences, and connected services."}>
      <Card>
        <View style={{ flexDirection: 'row', gap: theme.spacing(2), marginBottom: theme.spacing(1.5) }}>
          <Pressable
            onPress={handleEditAvatar}
            accessibilityRole="button"
            accessibilityLabel="Change avatar"
            accessibilityHint="Opens avatar selection to upload or pick a preset"
            style={({ pressed }) => ({
              opacity: pressed ? 0.8 : 1,
              alignSelf: 'flex-start',
            })}
          >
            {renderAvatar()}
          </Pressable>
          <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
            <SubHeading>{userDisplayName}</SubHeading>
            <Body color={theme.colors.textSecondary}>{locationDisplay}</Body>
            {firebaseUser?.email && (
              <Caption color={theme.colors.textSecondary}>{firebaseUser.email}</Caption>
            )}
            {memberSinceYear && (
              <Caption color={theme.colors.textSecondary}>
                Member since {memberSinceYear} · GDPR & CCPA preferences managed
              </Caption>
            )}
          </View>
        </View>
        <View style={{ gap: theme.spacing(1.5) }}>
          <Button 
            label="Edit profile" 
            variant="secondary" 
            onPress={handleEditProfile}
            accessibilityLabel="Edit profile"
            accessibilityHint="Edit your profile information and settings"
          />
          <Button
            label={loggingOut ? 'Logging out...' : 'Log out'}
            variant="ghost"
            disabled={loggingOut}
            onPress={confirmLogout}
            accessibilityLabel={loggingOut ? "Logging out" : "Log out"}
            accessibilityHint="Sign out of your Tripvio account"
            accessibilityState={{ disabled: loggingOut }}
          />
          <Button
            label={loggingOut ? 'Deleting account...' : 'Delete account'}
            variant="danger"
            disabled={loggingOut}
            onPress={handleDeleteAccount}
            accessibilityLabel={loggingOut ? 'Deleting account' : 'Delete account'}
            accessibilityHint="Permanently delete your Tripvio account and associated data"
            accessibilityState={{ disabled: loggingOut }}
            icon={<Ionicons name="trash-outline" size={18} color={theme.colors.onError} />}
          />
        </View>
      </Card>

      <Card variant="outlined">
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing(1) }}>
          <SubHeading>Plan & Access</SubHeading>
          <Caption color={premiumOnProfile ? theme.colors.success || theme.colors.primary : theme.colors.textSecondary}>
            {premiumOnProfile ? 'Premium' : 'Free'}
          </Caption>
        </View>
        <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
          {premiumOnProfile
            ? 'Premium unlocks unlimited active trips, offline mode, full email sync imports, and unlimited AI recommendations.'
            : `Free includes ${FREE_PLAN_LIMITS.maxActiveTrips} active trip, ${FREE_PLAN_LIMITS.aiTripRecommendationsPerMonth} Trips AI destination pick/month, up to ${FREE_PLAN_LIMITS.aiPlanIdeaCardsPerMonth} Plan AI idea cards/month, and ${FREE_PLAN_LIMITS.emailSyncTripsLifetime} email-sync trip.`}
        </Body>
        {offlinePlan.showOfflineHint ? (
          <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
            Showing last known plan while offline. Connect to refresh for the latest status.
          </Caption>
        ) : null}
        <View style={{ gap: theme.spacing(1) }}>
          {!premiumOnProfile ? (
            <Button
              label="Upgrade to Premium"
              onPress={() => openMarketingUpsell({ source: 'profile_plan_card' })}
              icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.surface} />}
            />
          ) : null}
          <Button
            label={refreshingEntitlement ? 'Refreshing status...' : 'Restore / Refresh purchase status'}
            variant="secondary"
            disabled={refreshingEntitlement}
            onPress={handleRefreshEntitlement}
            icon={<Ionicons name="refresh-outline" size={18} color={theme.colors.text} />}
          />
        </View>
      </Card>

      <BuildUpdateDiagnosticsCard />
      {(__DEV__ || isAdmin) && <WeatherLocationDebugCard />}
      {(__DEV__ || isAdmin) && <ApplePhase5DebugCard />}

      <Card variant="outlined" style={{ borderColor: theme.colors.primary + '40', borderWidth: 1.5 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(1.5) }}>
          <Ionicons name="shield-checkmark" size={24} color={theme.colors.primary} />
          <SubHeading>Privacy & Consent</SubHeading>
        </View>
        <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
          Manage your data privacy preferences, consent settings, and exercise your GDPR rights.
        </Body>
        <View style={{ gap: theme.spacing(1) }}>
          <Button
            label="Manage Consent Preferences"
            variant="primary"
            onPress={() => router.push('/(tabs)/consent-management')}
            icon={<Ionicons name="settings-outline" size={18} color={theme.colors.surface} />}
          />
          <Button
            label="Privacy Dashboard"
            variant="secondary"
            onPress={() => setShowPrivacyModal(true)}
            icon={<Ionicons name="shield-outline" size={18} color={theme.colors.text} />}
          />
        </View>
      </Card>

      <Card variant="outlined">
        <SubHeading>Preferences</SubHeading>
        <View style={{ gap: theme.spacing(1.5) }}>
          {preferences.map((item) => {
            const isPressable = Boolean(item.onPress);
            const content = (
              <View
                key={item.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                  paddingVertical: theme.spacing(0.5),
                }}
              >
                <Ionicons
                  name={item.icon}
                  size={20}
                  color={theme.colors.textSecondary}
                  style={{ width: 24 }}
                />
                <View style={{ flex: 1, gap: theme.spacing(0.25) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    <ThemedText variant="bodyStrong">{item.label}</ThemedText>
                    {item.status === 'loading' && (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    )}
                    {item.status === 'connected' && (
                      <Ionicons name="checkmark-circle" size={16} color={theme.colors.success || theme.colors.primary} />
                    )}
                    {item.status === 'disconnected' && (
                      <Ionicons name="close-circle" size={16} color={theme.colors.error || theme.colors.textSecondary} />
                    )}
                  </View>
                  <Caption color={theme.colors.textSecondary}>{item.value}</Caption>
                </View>
                {isPressable && (
                  <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
                )}
              </View>
            );

            if (isPressable) {
              return (
                <Pressable
                  key={item.id}
                  onPress={item.onPress}
                  style={({ pressed }) => ({
                    opacity: pressed ? 0.7 : 1,
                    minHeight: 44, // WCAG minimum touch target
                  })}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.label}: ${item.value}${item.status === 'connected' ? ', connected' : item.status === 'disconnected' ? ', not connected' : ''}. Tap to edit`}
                  accessibilityHint={item.id === 'integrations' 
                    ? "Manage connected services like email sync"
                    : "Edit this preference"}
                  accessibilityState={{ disabled: item.status === 'loading' }}
                >
                  {content}
                </Pressable>
              );
            }

            return content;
          })}
        </View>
      </Card>

      <Card>
        <SubHeading>Travel Statistics</SubHeading>
        <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
          Track your travel achievements, milestones, and explore the world.
        </Body>
        <View style={{ gap: theme.spacing(1) }}>
          <Button
            label="View Stats Dashboard"
            variant="secondary"
            onPress={() => router.push('/gamification/stats')}
          />
          <Button
            label="Countries map"
            variant="secondary"
            onPress={() => router.push('/gamification/travel-map')}
            icon={<Ionicons name="map-outline" size={18} color={theme.colors.text} />}
          />
          <Button
            label="Yearly summary"
            variant="secondary"
            onPress={() => router.push('/gamification/yearly-summary')}
            icon={<Ionicons name="sparkles-outline" size={18} color={theme.colors.text} />}
          />
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            <Button
              label="Achievements"
              variant="ghost"
              onPress={() => router.push('/gamification/achievements')}
              style={{ flex: 1 }}
            />
            <Button
              label="Milestones"
              variant="ghost"
              onPress={() => router.push('/gamification/milestones')}
              style={{ flex: 1 }}
            />
          </View>
        </View>
      </Card>

      {isAdmin && (
        <Card>
          <SubHeading>Template manager</SubHeading>
          <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
            Create and manage trip templates.
          </Body>
          <Button
            label="Manage templates"
            variant="secondary"
            onPress={() => router.push('/gamification/admin-templates')}
            icon={<Ionicons name="document-text-outline" size={18} color={theme.colors.text} />}
          />
        </Card>
      )}

      <Card>
        <SubHeading>Offline Mode</SubHeading>
        <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
          Download maps, documents, itinerary, and safety info for offline access. Manage auto-download settings.
        </Body>
        <Button
          label="Manage Offline Content"
          variant="secondary"
          onPress={() => {
            if (!premiumOnProfile) {
              openPremiumGate({
                feature: 'offline_mode',
                title: 'Offline Mode',
                message: 'Offline Mode is a Premium feature. Upgrade to download maps, itineraries, and documents for offline use.',
                source: 'feature_gate',
              });
              return;
            }
            setShowOfflineModal(true);
          }}
          icon={<Ionicons name="cloud-download-outline" size={18} color={theme.colors.text} />}
        />
      </Card>

      <Card>
        <SubHeading>Storage Management</SubHeading>
        <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
          View and manage your offline cache, including documents, trips, and other cached data.
        </Body>
        <Button
          label="Manage Storage"
          variant="secondary"
          onPress={() => setShowStorageModal(true)}
          icon={<Ionicons name="server-outline" size={18} color={theme.colors.text} />}
        />
      </Card>

      <HelpAndFeedbackCard />

      {/* Privacy Dashboard Modal */}
      <Modal
        visible={showPrivacyModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowPrivacyModal(false)}
      >
        <Screen
          title="Privacy Dashboard"
          subtitle="Manage your data and privacy settings"
          headerAction={
            <Pressable 
              onPress={() => setShowPrivacyModal(false)}
              style={{ minHeight: 44, minWidth: 44, alignItems: 'center', justifyContent: 'center' }}
              accessibilityRole="button"
              accessibilityLabel="Close privacy dashboard"
              accessibilityHint="Closes the privacy dashboard modal"
            >
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          }
        >
          <Card>
            <SubHeading>Data Export</SubHeading>
            <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
              Download all your Tripvio data including trips, bookings, expenses, and documents in a portable format.
            </Body>
            <Button
              label={exportingData ? "Exporting..." : "Export my data"}
              variant="secondary"
              disabled={exportingData}
              onPress={async () => {
                try {
                  setExportingData(true);
                  await exportAndDownloadUserData();
                } catch (error) {
                  // Error is already handled in exportAndDownloadUserData
                  console.error('[Profile] Error exporting data:', error);
                } finally {
                  setExportingData(false);
                }
              }}
            />
          </Card>

          <Card variant="outlined">
            <SubHeading>Account Deletion</SubHeading>
            <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
              Permanently delete your account and all associated data. This action cannot be undone. All your trips, bookings, expenses, documents, and other data will be permanently deleted.
            </Body>
            <Button
              label="Delete my account"
              variant="ghost"
              onPress={handleDeleteAccount}
              disabled={loggingOut}
            />
          </Card>

          <Card>
            <SubHeading>Consent Management</SubHeading>
            <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
              Manage your consent preferences for data processing, marketing communications, and analytics.
            </Body>
          </Card>

          <Card variant="outlined">
            <SubHeading>Data Protection Officer</SubHeading>
            <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
              Contact our Data Protection Officer for privacy-related inquiries or to exercise your rights under GDPR and CCPA.
            </Body>
            <Button
              label="Contact DPO"
              variant="secondary"
              onPress={() => {
                Alert.alert(
                  'Contact Data Protection Officer',
                  'Please email privacy@tripvio.app with your inquiry. We will respond within 30 days as required by GDPR.'
                );
              }}
            />
          </Card>
        </Screen>
      </Modal>

      {/* Theme / Appearance Modal */}
      <Modal
        visible={showThemeModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowThemeModal(false)}
      >
        <Screen
          title="Appearance"
          subtitle="Light, dark, or match your device"
          headerAction={
            <Pressable onPress={() => setShowThemeModal(false)}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          }
        >
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(0) }}>
              {(['light', 'dark', 'system'] as const).map((option) => {
                const label =
                  option === 'light' ? 'Light' : option === 'dark' ? 'Dark' : 'System (use device setting)';
                const isSelected = themePreference === option;
                return (
                  <Pressable
                    key={option}
                    onPress={() => {
                      setThemePreference(option);
                      setShowThemeModal(false);
                    }}
                    style={({ pressed }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingVertical: theme.spacing(1.5),
                      paddingHorizontal: theme.spacing(2),
                      opacity: pressed ? 0.7 : 1,
                      minHeight: 44,
                    })}
                    accessibilityRole="radio"
                    accessibilityState={{ checked: isSelected }}
                    accessibilityLabel={label}
                  >
                    <Body>{label}</Body>
                    {isSelected && (
                      <Ionicons name="checkmark-circle" size={22} color={theme.colors.primary} />
                    )}
                  </Pressable>
                );
              })}
            </View>
          </Card>
        </Screen>
      </Modal>

      {/* Offline Settings Modal */}
      <Modal
        visible={showOfflineModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowOfflineModal(false)}
      >
        <Screen
          title="Offline Mode"
          subtitle="Manage offline content and auto-download settings"
          scrollable={false}
          headerAction={
            <Pressable onPress={() => setShowOfflineModal(false)}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          }
        >
          <OfflineSettingsScreen embedded />
        </Screen>
      </Modal>

      {/* Storage Management Modal */}
      <Modal
        visible={showStorageModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowStorageModal(false)}
      >
        <Screen
          title="Storage Management"
          subtitle="Manage your offline cache and storage"
          headerAction={
            <Pressable onPress={() => setShowStorageModal(false)}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          }
        >
          <TripErrorBoundary name="StorageManagement">
            <Suspense fallback={null}>
              <StorageManagement />
            </Suspense>
          </TripErrorBoundary>
        </Screen>
      </Modal>

      {/* Upgrade / purchase flow: shown when user taps "Upgrade" in premium gate modal */}
      <Modal
        visible={showUpgradeFlowModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowUpgradeFlowModal(false)}
      >
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', padding: theme.spacing(3) }}
          onPress={() => setShowUpgradeFlowModal(false)}
        >
          <Pressable onPress={() => {}} style={{ width: '100%', maxWidth: 420, alignSelf: 'center' }}>
            <Card variant="outlined" padding={theme.spacing(2.5)}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing(1.5) }}>
                <SubHeading>Choose how to upgrade</SubHeading>
                <Pressable onPress={() => setShowUpgradeFlowModal(false)} accessibilityRole="button" accessibilityLabel="Close">
                  <Ionicons name="close" size={22} color={theme.colors.textSecondary} />
                </Pressable>
              </View>
              <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(2) }}>
                {isExternalCheckoutAllowed()
                  ? 'Subscribe on web or in the app, then use Restore if you already have Premium.'
                  : 'Subscribe in app, then use Restore if you already have Premium.'}
              </Body>
              <View style={{ gap: theme.spacing(1) }}>
                {isExternalCheckoutAllowed() ? (
                  <>
                    <Caption color={theme.colors.textSecondary}>Choose a web billing plan</Caption>
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                      <Pressable
                        onPress={() => setWebCheckoutInterval('monthly')}
                        accessibilityRole="button"
                        accessibilityLabel="Select monthly web plan"
                        style={{
                          flex: 1,
                          minHeight: 44,
                          borderRadius: theme.radii.md,
                          borderWidth: 1,
                          borderColor:
                            webCheckoutInterval === 'monthly' ? theme.colors.primary : theme.colors.borderMuted,
                          backgroundColor:
                            webCheckoutInterval === 'monthly' ? `${theme.colors.primary}22` : theme.colors.surface,
                          alignItems: 'center',
                          justifyContent: 'center',
                          paddingVertical: theme.spacing(1),
                        }}
                      >
                        <Caption style={{ fontWeight: '700' }}>Monthly - $5.99 USD</Caption>
                      </Pressable>
                      <Pressable
                        onPress={() => setWebCheckoutInterval('annual')}
                        accessibilityRole="button"
                        accessibilityLabel="Select annual web plan"
                        style={{
                          flex: 1,
                          minHeight: 44,
                          borderRadius: theme.radii.md,
                          borderWidth: 1,
                          borderColor:
                            webCheckoutInterval === 'annual' ? theme.colors.primary : theme.colors.borderMuted,
                          backgroundColor:
                            webCheckoutInterval === 'annual' ? `${theme.colors.primary}22` : theme.colors.surface,
                          alignItems: 'center',
                          justifyContent: 'center',
                          paddingVertical: theme.spacing(1),
                        }}
                      >
                        <Caption style={{ fontWeight: '700' }}>Annual - $39.99 USD</Caption>
                      </Pressable>
                    </View>
                    <Caption color={theme.colors.textSecondary}>
                      {webCheckoutInterval === 'annual'
                        ? 'Annual plan: billed once per year.'
                        : 'Monthly plan: billed every month.'}
                    </Caption>
                    <Button
                      label={
                        startingCheckout
                          ? `Opening ${startingCheckout} checkout...`
                          : `Continue on web (${webCheckoutInterval === 'annual' ? 'Annual' : 'Monthly'})`
                      }
                      disabled={startingCheckout !== null}
                      onPress={async () => {
                        if (networkMonitor.shouldUseOfflineDataFallback()) {
                          Alert.alert('Offline', 'Reconnect to the internet to open checkout.');
                          return;
                        }
                        try {
                          setStartingCheckout(webCheckoutInterval);
                          const { url } = await createStripeCheckoutSession(webCheckoutInterval);
                          await Linking.openURL(url);
                          setShowUpgradeFlowModal(false);
                        } catch (e) {
                          console.error('[Profile] Stripe checkout failed', e);
                          Alert.alert('Checkout', 'Could not open checkout. Please try again or use Restore if you already subscribed.');
                        } finally {
                          setStartingCheckout(null);
                        }
                      }}
                      icon={<Ionicons name="open-outline" size={18} color={theme.colors.surface} />}
                    />
                  </>
                ) : null}
                {Platform.OS !== 'web' ? (
                  <Button
                    label={presentingPaywall ? 'Opening paywall...' : 'Subscribe in app'}
                    variant="primary"
                    disabled={presentingPaywall}
                    onPress={async () => {
                      if (networkMonitor.shouldUseOfflineDataFallback()) {
                        Alert.alert('Offline', 'Reconnect to the internet to open in-app subscriptions.');
                        return;
                      }
                      if (presentingPaywall) return;
                      setPresentingPaywall(true);
                      try {
                        const result = await presentRevenueCatPaywall(firebaseUser?.uid || auth.currentUser?.uid);
                        if (result.ok) {
                          logger.info('[Profile] RevenueCat paywall completed', {
                            outcome: result.outcome,
                            detail: result.detail,
                          });
                          if (result.outcome === 'purchased' || result.outcome === 'restored') {
                            await handleRefreshEntitlement();
                            setShowUpgradeFlowModal(false);
                          }
                        } else {
                          const rc = getRevenueCatInitStatus();
                          logger.warn('[Profile] RevenueCat paywall not presented', {
                            reason: result.reason,
                            detail: result.detail,
                            revenueCatStatus: rc.status,
                            revenueCatInitialized: rc.initialized,
                          });
                          if (result.reason === 'present_failed' && result.detail) {
                            captureException(new Error(result.detail), { kind: 'paywall_present_failed' });
                          }
                          if (result.reason === 'init_failed' && result.detail) {
                            captureException(new Error(result.detail), { kind: 'revenuecat_configure_failed' });
                          }
                          const { title, message } = paywallFailureAlert(result);
                          Alert.alert(title, message);
                        }
                      } catch (error) {
                        const err = error instanceof Error ? error : new Error(String(error));
                        logger.warn('[Profile] RevenueCat paywall failed to present', undefined, err);
                        captureException(err, { kind: 'paywall_present_failed' });
                        Alert.alert(
                          'In-app subscription',
                          'We could not open the subscription sheet. Please try again, or use Restore purchases if you already subscribed.',
                        );
                      } finally {
                        setPresentingPaywall(false);
                      }
                    }}
                    icon={<Ionicons name="phone-portrait-outline" size={18} color={theme.colors.surface} />}
                  />
                ) : null}
                <Button
                  label={refreshingEntitlement ? 'Refreshing...' : 'Restore purchases'}
                  variant="secondary"
                  disabled={refreshingEntitlement}
                  onPress={async () => {
                    if (networkMonitor.shouldUseOfflineDataFallback()) {
                      Alert.alert('Offline', 'Reconnect to the internet to restore purchases.');
                      return;
                    }
                    await handleRefreshEntitlement();
                    setShowUpgradeFlowModal(false);
                  }}
                  icon={<Ionicons name="refresh-outline" size={18} color={theme.colors.text} />}
                />
              </View>
            </Card>
          </Pressable>
        </Pressable>
      </Modal>

      <ConnectedServicesModal
        visible={showConnectedServices}
        onClose={() => setShowConnectedServices(false)}
      />

      <NotificationSettingsModal
        visible={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
        value={profile.notificationPreference}
        onSave={async (next) => {
          const profileUpdate = { notificationPreference: next };

          // Fail-open: if no auth user, save locally.
          if (!firebaseUser?.uid) {
            setProfileDetails(profileUpdate);
            return;
          }

          try {
            await setDoc(
              doc(db, 'users', firebaseUser.uid),
              {
                notificationPreference: next ?? null,
                updatedAt: serverTimestamp(),
              },
              { merge: true },
            );
            setProfileDetails(profileUpdate);
          } catch (error) {
            console.error('[Profile] Failed to save notification preferences', error);
            Alert.alert(
              'Saving offline',
              'Could not save to the server right now, but we will keep your preferences locally.',
            );
            setProfileDetails(profileUpdate);
          }
        }}
      />
    </Screen>
  );
}

export default function ProfileScreen(props: ProfileScreenProps = {}) {
  return (
    <TripErrorBoundary name="Profile">
      <ProfileScreenInner {...props} />
    </TripErrorBoundary>
  );
}
